import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useDemo } from "@/store/DemoStore";
import { api, errMsg } from "@/lib/api";
import { Card, Logo, btn, normalizePhone, isPhoneKey, normalizePhoneOnBlur } from "@/components/common";

const field = "h-11 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-base text-navy-900 outline-none focus:border-navy-500 focus:ring-2 focus:ring-navy-100 disabled:bg-slate-50 disabled:text-slate-500 sm:text-sm";

export default function InviteAccept() {
  const { token } = useParams();
  const { refresh } = useDemo();
  const nav = useNavigate();
  const [inv, setInv] = useState(null);
  const [error, setError] = useState("");
  const [f, setF] = useState({ name: "", phone: "", city: "", password: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/invites/${token}`).then(({ data }) => { setInv(data); setF((x) => ({ ...x, name: data.name || "", phone: data.phone || "", city: data.city || "" })); }).catch((e) => setError(errMsg(e)));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (f.password !== f.confirm) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      await api.post(`/invites/${token}/accept`, { name: f.name, phone: f.phone, city: f.city, password: f.password });
      await refresh();
      toast.success("Welcome to MyTrip!");
      nav("/mytrip", { replace: true });
    } catch (err) {
      toast.error(errMsg(err));
    }
    setBusy(false);
  };

  const set = (k) => (e) => setF({ ...f, [k]: isPhoneKey(k) ? normalizePhone(e.target.value) : e.target.value });
  return (
    <div className="app-shell scroll-area bg-[#F5F7FA]" data-testid="invite-accept-page">
      <div className="mx-auto flex min-h-full max-w-md flex-col justify-center px-4 py-10">
        <div className="mb-6 flex justify-center"><Logo brand="mytrip" size="h-14" /></div>
        <Card className="p-6 sm:p-8">
          {error ? <p className="text-center text-sm text-slate-600" data-testid="invite-error">{error}</p> : !inv ? <div className="grid place-items-center py-10"><Loader2 className="h-6 w-6 animate-spin text-navy-700" /></div> : (
            <>
              <h1 className="font-display text-2xl font-bold text-navy-900">Create your account</h1>
              <p className="mt-1 text-sm text-slate-500">Your advisor invited you to MyTrip. Confirm your details and choose a password.</p>
              <form onSubmit={submit} className="mt-6 space-y-3">
                <label className="block"><span className="mb-1.5 block text-xs font-medium text-slate-500">Email</span><input disabled value={inv.email} className={field} data-testid="invite-email" /></label>
                {[["name", "Full name", "text", true], ["phone", "Phone", "tel"], ["city", "City", "text"]].map(([k, l, t, req]) => (
                  <label key={k} className="block"><span className="mb-1.5 block text-xs font-medium text-slate-500">{l}</span><input required={req} type={t} value={f[k]} onChange={set(k)} onBlur={isPhoneKey(k) ? normalizePhoneOnBlur((n) => setF((p) => ({ ...p, [k]: n }))) : undefined} className={field} data-testid={`invite-${k}`} /></label>
                ))}
                {[["password", "Password (8+ characters)"], ["confirm", "Confirm password"]].map(([k, l]) => (
                  <label key={k} className="block"><span className="mb-1.5 block text-xs font-medium text-slate-500">{l}</span><input required minLength={8} type="password" autoComplete="new-password" value={f[k]} onChange={set(k)} className={field} data-testid={`invite-${k}`} /></label>
                ))}
                <button disabled={busy} className={`${btn.primary} h-11 w-full`} data-testid="invite-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}</button>
              </form>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
